import React, { useState, useEffect } from "react";
import { Row, Col, Typography, Button, message, Table, Tooltip, Form, Modal, Upload, Space } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined, FileImageOutlined, DownloadOutlined } from "@ant-design/icons";
import http from "../../../../../services/http";
const { Text } = Typography;

export const SugestoesAnexos = ({ idSugestao, status }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [dados, setDados] = useState([]);
	const [modal, contextHolder] = Modal.useModal();
	const [showModal, setShowModal] = useState(false);

	useEffect(() => {
		if (idSugestao > 0) {
			listarSugestoesAnexos(idSugestao);
		}
	}, [idSugestao]);

	// LISTAR
	const listarSugestoesAnexos = async (id_sugestao) => {
		setLoading(true);
		const api = await http("get", `v1/sugestoes_anexos/listar/${id_sugestao}`, []);
		if (api.statusCode === 200) {
			setDados(api?.response?.data);
		} else {
			message.error(api.response.message);
		}
		setLoading(false);
	};

	// CADASTRAR
	const cadastrarSugestaoAnexo = async (formulario) => {
		if (!formulario.id_sugestoes) return;
		setLoading(true);
		let erros = 0;

		await Promise.all(
			formulario.arquivos.map(async (arquivo) => {
				const formData = new FormData();
				formData.append("id_sugestoes", formulario.id_sugestoes);
				formData.append("file", arquivo.originFileObj);

				const api = await http("post", "v1/sugestoes_anexos/adicionar", formData, "multipart/form-data");
				if (api.statusCode !== 200) {
					erros++;
				}
			})
		);

		if (erros > 0) {
			message.error("Houve algum erro ao tentar enviar!");
		}

		listarSugestoesAnexos(idSugestao);
		setShowModal(false);
		resetForm();
		setLoading(false);
	};

	// DELETAR
	const deletarSugestaoAnexo = async (id) => {
		setLoading(true);
		const api = await http("post", `v1/sugestoes_anexos/deletar`, { id: id });
		if (api.statusCode === 200) {
			message.success("Registro deletado com sucesso!");
			listarSugestoesAnexos(idSugestao);
		} else {
			message.error(api.response.message);
		}
		setLoading(false);
	};

	// CARREGAR
	const carregarSugestaoAnexo = async (id) => {
		setLoading(true);
		const api = await http("get", `v1/sugestoes_anexos/carregar/${id}`, []);
		if (api.statusCode === 200) {
			// DOWNLOAD FILE
			let a = document.createElement("a");
			a.href = "data:application/octet-stream;base64," + api?.response?.data?.arquivo;
			a.download = api?.response?.data?.nome;
			a.click();
		} else {
			message.error(api.response.message);
		}
		setLoading(false);
	};

	// RESET
	const resetForm = () => {
		form.resetFields();
	};

	// COLUNAS
	const columns = [
		{
			title: "Nº",
			dataIndex: "id",
			sorter: (a, b) => a.id?.toString()?.localeCompare(b.id?.toString()),
		},
		{
			title: "Nome",
			dataIndex: "nome",
			sorter: (a, b) => a.nome?.localeCompare(b.nome),
		},
		{
			title: "Ações",
			width: 80,
			render: (_, e) => (
				<Space.Compact>
					<Tooltip title="Baixar">
						<Button loading={loading} onClick={() => carregarSugestaoAnexo(e.id)} icon={<DownloadOutlined />} />
					</Tooltip>
					{status === 1 && (
						<Tooltip title="Excluir">
							<Button
								loading={loading}
								icon={<DeleteOutlined />}
								onClick={() => {
									modal.confirm({
										title: "Deseja deletar este registro?",
										okText: "Sim",
										cancelText: "Cancelar",
										onOk() {
											deletarSugestaoAnexo(e.id);
										},
									});
								}}
							/>
						</Tooltip>
					)}
				</Space.Compact>
			),
			dataIndex: "acoes",
		},
	];

	// SUBMIT FORM
	const onSubmit = (formulario) => {
		formulario.id_sugestoes = idSugestao;
		cadastrarSugestaoAnexo(formulario);
	};

	// GET FILE
	const formFile = (e) => {
		if (Array.isArray(e)) {
			return e;
		}
		return e?.fileList;
	};

	// FORMULÁRIO
	const modalFormulario = () => {
		return (
			<Modal
				title={
					<Text style={{ fontSize: 20 }}>
						<EditOutlined /> Novo Documento (PDF)
					</Text>
				}
				open={showModal}
				onOk={() => form.submit()}
				okText="Salvar"
				width={1200}
				okButtonProps={{ size: "large", loading: loading }}
				cancelButtonProps={{ size: "large", loading: loading }}
				onCancel={() => {
					setShowModal(false);
					resetForm();
				}}>
				<Form form={form} onFinish={onSubmit} layout="vertical" autoComplete="off">
					<Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
						<Col className="gutter-row" span={24}>
							<Form.Item
								name="arquivos"
								valuePropName="fileList"
								getValueFromEvent={formFile}
								rules={[{ required: true, message: "Adicione um arquivo!" }]}>
								<Upload.Dragger
									style={{ maxHeight: 300 }}
									disabled={loading}
									multiple
									beforeUpload={() => false}
									accept=".pdf">
									<p className="ant-upload-drag-icon">
										<FileImageOutlined />
									</p>
									<p className="ant-upload-text">Clique ou arraste uma imagem para esta área para fazer upload</p>
									<p className="ant-upload-text">Adicione arquivos no formato PDF</p>
								</Upload.Dragger>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Modal>
		);
	};

	return (
		<React.Fragment>
			{modalFormulario()}
			<Space direction="vertical" style={{ width: "100%" }}>
				<Tooltip title="Adicionar Registro">
					{status === 1 && (
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => {
								setShowModal(true);
								resetForm();
							}}
							loading={loading}>
							Novo
						</Button>
					)}
				</Tooltip>

				<Table rowKey="id" loading={loading} scroll={{ x: 700 }} columns={columns} dataSource={dados} size="small" />
			</Space>
			{contextHolder}
		</React.Fragment>
	);
};
